import * as React from "react";
import { withReturnJob } from "../../hooks/useInAppHistory";
import { Form, Formik } from "formik";
import { Button, List, ListItemSecondaryAction, ListItemText, Paper, Typography } from "@material-ui/core";
import { ContactListItem } from "../ListItems/ContactListItem";
import { AppointmentListItem } from "../ListItems/AppointmentListItem";
import { ServiceLocationListItem } from "../ListItems/ServiceLocationListItem";
import { defaultTo, equals, prop, anyPass, isEmpty, isNil, startsWith, uniqBy, reject, pluck } from "ramda";
import ListItem from "@material-ui/core/ListItem";
import { ListItemWithSubList } from "../ListItems/ListItemWithSubList";
import { LinkListItem } from "../ListItems/LinkListItem";
import { VehicleInfoListItem } from "../ListItems/VehicleInfoListItem";
import { useHasVehicleHistory } from "../../hooks/useUiv";
import { withLatestVehicleFields } from "../../lib/jobVehicleFields.utils";
import { VEHICLE_HISTORY_MOUNT, VehicleHistoryMount, vehicleHistoryPath } from "../../lib/vehicleHistory.utils";
import { ItemsField } from "./fields/ItemsField";
import { FilesListItem } from "../ListItems/FilesListItem";
import {
  Appointment,
  Contact,
  Item,
  JobFeedback,
  PartsStore,
  Query,
  PromoCode,
  ViocStore,
  QueryGetVehicleArgs,
  Job,
  PartOrderItemV2,
} from "../../generated/nest-graphql";
import { VehicleInfoValues } from "../../types/VehicleInfoValues";
import * as Yup from "yup";
import { TextAreaField } from "./fields/TextAreaField";
import Container from "@material-ui/core/Container";
import Divider from "@material-ui/core/Divider";
import { BottomSubmitButtonRow } from "../Buttons/BottomSubmitButtonRow";
import { PartsInfoListItem } from "../ListItems/PartsInfoListItem";
import { VehicleInfoSymptomsFormSection } from "./VehicleInfoSymptomsFormSection";
import { JobTypeListItem } from "../ListItems/JobTypeListItem";
import { flow, pipe } from "fp-ts/lib/function";
import { PartsStoreListItem } from "../ListItems/PartsStoreListItem";
import { useQuery } from "@apollo/client";
import { TECHNICIAN_GET_ME } from "../../graphql/queries/technicianGetMe";
import { PartsOrderingSurveyFormValues } from "./Surveys/PartsOrderingSurveyForm";
import { ServicesSection } from "../services/ServicesSection";
import { JOB, SERVICE_CATALOGUE_USED_ITEMS, SERVICE_CATALOGUE_USED_SERVICES } from "../../lib/constants";
import { PreJobCheckListFormValues } from "../Jobs/PreJobCheckListDialog";
import { JobViocStoreSelectField } from "./fields/JobViocStoreSelectField";
import { PreJobCheckListV2FormValues } from "../PreJobChecklistV2/PreJobCheckListV2Dialog";
import { GET_VEHICLE } from "../../graphql/queries/getVehicles";
import { showErrorAlert, showSuccessAlert } from "../../actions";
import { useAxiosClient } from "../../lib/nestBackendClient";
import { useDispatch } from "../../contexts/snackbar-context";
import { groupServicesByGrade } from "../PreJobChecklistV2/colorGradeHelpers";
import { useMultiplePartOrders } from "../../hooks/useMultiplePartOrders";
import { GET_PART_ORDERS } from "../../graphql/queries/getPartOrders";

export type EJIItemFormValue = {
  id?: string;
  serviceProductId: string;
  productSelection?: {
    name?: string;
    part?: {
      id?: string;
      partId?: number;
      productName?: string;
      label?: string;
      vendorUnitCost?: number;
      type?: string;
    };
  };
  type: string;
  units?: number;
  overriddenCustomerPrice: boolean;
  customerPrice: number;
  vendorUnitCost?: number;
  expectedVendorUnitCost?: number;
  behavior: string;
  category: string;
  notes?: string;
  orderItem?: {
    partsStore?: PartsStore;
    isOrdered?: Boolean;
    partNumber?: String;
  };
  marketVendorUnitCostMultiplierUsed?: number;
  marketLaborRateUsed?: number;
  jobItem?: PartOrderItemV2;
};

export type PartsCalcs = {
  marketVendorUnitCostMultiplierUsed?: number;
  overriddenCustomerPrice?: boolean;
  totalPrice?: string;
  vendorCost?: string;
};

export type EJIServiceFormValue = {
  id?: string;
  service: string;
  name: string;
  inEstimate: boolean;
  customerPrice: number;
  technicianPay: number;
  partsCalcs: PartsCalcs;
  items: EJIItemFormValue[];
  updatedAt?: Date;
  createdAt?: Date;
  inInvoice?: boolean;
  needsRecalculation?: boolean;
};

export type EJIDiscountFormValue = {
  type: string;
  amount: number;
  reason: string;
  total?: number;
};

export type PromoCodeFormValue = {
  code: string;
  name: string;
  coupon: {
    name: string;
    valid: boolean;
    discount: EJIDiscountFormValue;
  };
};

export type EJIVendorUnitCostMultiplierFormValue = {
  startRangePrice?: number;
  endRangePrice?: number;
  multiplier?: number;
};

export type EJIPricingConfigFormValue = {
  laborRate?: number;
  vendorUnitCostMultipliers?: EJIVendorUnitCostMultiplierFormValue[];
};

export type EJIPriceInfoFormValue = {
  pricingConfig?: EJIPricingConfigFormValue;
  partsTotal?: number;
  laborTotal?: number;
  feesTotal?: number;
  subTotal?: number;
  discounts?: EJIDiscountFormValue[];
  promoCodes?: PromoCode[];
  discountTotal?: number;
  finalPartsTotal?: number;
  finalLaborTotal?: number;
  finalFeesTotal?: number;
  finalSubTotal?: number;
  partsTax?: number;
  laborTax?: number;
  totalTax?: number;
  amountDue?: number;
};

export type JobDetailsFormValues = VehicleInfoValues &
  JobFeedback & {
    jobId?: string;
    partsOrderingIssuesSurvey?: PartsOrderingSurveyFormValues;
    partsOrderingIssuesSurveys: PartsOrderingSurveyFormValues[];
    contact: Contact;
    market: string;
    taxable: boolean;
    estimate?: string;
    serviceLocation: string;
    status: string;
    type: string;
    serviceCallReason: string;
    serviceCallReasonExtraInfo: string;
    warrantyCheckReason: string;
    warrantyCheckReasonExtraInfo: string;
    warrantyCheckTechReason: string;
    warrantyCheckTechReasonExtraInfo: string;
    appointmentId: string;
    jobName: string;
    vehicleId: string;
    privateNotes?: string;
    serviceLocationNotes?: string;
    description?: string;
    viocStore?: ViocStore;
    items: Item[];
    services: EJIServiceFormValue[];
    discounts?: EJIDiscountFormValue[];
    promoCodes?: PromoCodeFormValue[];
    priceInfo?: EJIPriceInfoFormValue;
    serviceCatalogueUsed?: string;
    partsOrderNumber?: string;
    partsLocation?: string;
    partsNotes?: string;
    partsOrdered?: boolean;
    preJobCheckList?: PreJobCheckListFormValues;
    preJobCheckListV2?: PreJobCheckListV2FormValues;
    locationType?: string;
  };

const jobDetailsFormValidationSchema = (useMultiplePartOrders: boolean) =>
  Yup.object().shape({
    status: Yup.string().required("Required"),
    contact: Yup.object().required("Required"),
    serviceLocation: Yup.string().required("Required"),
    type: Yup.string().required("Required"),
    serviceCallReason: Yup.string().when("type", {
      is: flow(defaultTo(""), startsWith("Service")),
      then: Yup.string().required("Must Provide a Service Call Reason"),
      otherwise: Yup.string().nullable(),
    }),
    serviceCallReasonExtraInfo: Yup.string().when(["type", "serviceCallReason"], {
      is: (type, reason) =>
        flow(defaultTo(""), startsWith("Service"))(type) && flow(defaultTo(""), startsWith("Other"))(reason),
      then: Yup.string().required("Must Provide Additional Details"),
      otherwise: Yup.string().nullable(),
    }),
    warrantyCheckTechReason: Yup.string().when("type", {
      is: flow(defaultTo(""), startsWith("Warranty Check")),
      then: Yup.string().required("Must Provide a Warranty Check Reason"),
      otherwise: Yup.string().nullable(),
    }),
    warrantyCheckTechReasonExtraInfo: Yup.string().when(["type", "warrantyCheckTechReason"], {
      is: (type, reason) =>
        flow(defaultTo(""), startsWith("Warranty Check"))(type) && flow(defaultTo(""), startsWith("Other"))(reason),
      then: Yup.string().required("Must Provide Additional Details"),
      otherwise: Yup.string().nullable(),
    }),
    jobName: Yup.string().required("Required"),
    services: Yup.array().of(
      Yup.object().shape({
        items: Yup.array().of(
          Yup.object().shape({
            type: Yup.string().required(),
            productSelection: Yup.object().when("category", {
              is: equals("Part"),
              then: Yup.object()
                .shape({
                  name: Yup.string().required(),
                })
                .required(),
              otherwise: Yup.object()
                .shape({
                  name: Yup.string().notRequired(),
                })
                .nullable()
                .notRequired(),
            }),
            orderItem: Yup.object().when("category", {
              is: equals("Part"),
              then: Yup.object()
                .shape({
                  partNumber: Yup.string().nullable().notRequired(),
                })
                .required(),
              otherwise: Yup.object().nullable().notRequired(),
            }),
            customerPrice: Yup.string().required(),
            vendorUnitCost: !useMultiplePartOrders
              ? Yup.string()
                  .nullable()
                  .when("category", { is: (val) => val === "Part", then: Yup.string().required() })
              : undefined,
            expectedVendorUnitCost: useMultiplePartOrders
              ? Yup.string()
                  .nullable()
                  .when("category", { is: (val) => val === "Part", then: Yup.string().required() })
              : undefined,
          })
        ),
      })
    ),
  });

type JobDetailsFormProps = {
  initialValues: JobDetailsFormValues;
  job?: Job;
  onSubmit: (values: any, helpers: any) => void;
  appointment: Appointment;
  fileNames: string[];
  jobId: any;
  toggleSelectedServices?: (index: number) => void;
  /** The beta's Vehicle tab shows the vehicle and its history, so the Job tab drops those rows. */
  vehicleInOwnTab?: boolean;
};

export const JobDetailsForm: React.FC<JobDetailsFormProps> = ({
  initialValues,
  onSubmit,
  appointment,
  fileNames,
  jobId,
  job,
  toggleSelectedServices,
  vehicleInOwnTab,
}) => {
  const { data: technicianGetMeData } = useQuery<Query>(TECHNICIAN_GET_ME);
  const hasVehicleHistory = useHasVehicleHistory();
  const mountedValues = React.useRef(initialValues);

  const { data, loading } = useQuery<Query, QueryGetVehicleArgs>(GET_VEHICLE, {
    variables: {
      id: initialValues.vehicleId,
    },
  });

  const [sendingLink, setSendingLink] = React.useState(false);

  const dispatch = useDispatch();
  const nestBackendClient = useAxiosClient();

  const sendDIgitalInspection = async () => {
    setSendingLink(true);
    try {
      const response = await nestBackendClient.post("job-appointments/send-digital-inspection", {
        jobId,
        contact: data.getJob.contact.id,
        phoneNumber: data.getJob.contact.phoneNumber,
      });
      showSuccessAlert(dispatch, "Inspection sended successfully");
    } catch (error) {
      const isArray = Array.isArray(error?.response?.data?.message);
      const message = (isArray ? error?.response?.data?.message?.[0] : error?.response?.data?.message) || error.message;
      showErrorAlert(dispatch, message);
    }
    setSendingLink(false);
  };

  const { soon, immediatly } = groupServicesByGrade(job);

  const useMultiplePartOrdersValue = useMultiplePartOrders(job?.createdAt);
  const { data: partOrdersData } = useQuery<Query>(GET_PART_ORDERS, {
    variables: {
      filter: {
        job: job?.id,
      },
    },
  });

  return (
    <Formik<JobDetailsFormValues>
      initialValues={initialValues}
      enableReinitialize={false}
      validationSchema={jobDetailsFormValidationSchema(useMultiplePartOrdersValue)}
      onSubmit={(values, helpers) => {
        // Debug: inspect payload being sent on Update Job
        console.log("[JobDetailsForm] onSubmit values:", values);
        return onSubmit(
          vehicleInOwnTab ? withLatestVehicleFields(values, mountedValues.current, initialValues) : values,
          helpers
        );
      }}
    >
      {({ isValid, isSubmitting, setFieldValue, values, submitForm, setValues, touched }) => {
        const {
          contact,
          description,
          jobName,
          serviceLocation,
          serviceLocationNotes,
          type,
          vehicleId,
          customerExpectation,
          items,
          services,
          partsOrderNumber,
          partsLocation,
          partsNotes,
          locationType,
        } = values;
        const showVehicleHistoryRow =
          hasVehicleHistory && VEHICLE_HISTORY_MOUNT === VehicleHistoryMount.PAGE && !!vehicleId;

        const partOrders = (
          useMultiplePartOrdersValue
            ? partOrdersData?.getPartOrders?.data?.map((partOrder) => ({
                partsOrderNumber: partOrder.orderNumber,
                location: partsLocation,
                notes: partOrder.notes,
              }))
            : [
                {
                  partsOrderNumber: partsOrderNumber,
                  location: partsLocation,
                  notes: partsNotes,
                },
              ]
        )?.filter((partOrder) => partOrder.partsOrderNumber);
        const dirty = !!Object.keys(touched).length;
        // Previous logic ignored changes under the "services" key when deciding if the form was dirty:
        // const dirty = !!Object.keys(touched)
        //   .map((key) => ({ key, value: touched[key] }))
        //   .filter(({ key, value }) => key !== "services" && value).length;
        //
        // New logic: treat non-services touched fields as before, and also mark the form dirty
        // if the services array has actually changed compared to initialValues.
        const dirtyNonServices = !!Object.keys(touched)
          .map((key) => ({ key, value: touched[key] }))
          .filter(({ key, value }) => key !== "services" && value).length;
        const servicesChanged = !equals(values.services, initialValues.services);
        const hasChanges = dirtyNonServices || servicesChanged;
        const wheelTorque = !loading && data?.getVehicle?.vehicleTechnicalInfo?.wheelTorque;
        // Optional spec: vehicles without a recorded capacity omit the row entirely
        // rather than showing a blank or a misleading 0.
        const oilCapacityQuarts = !loading && data?.getVehicle?.vehicleTechnicalInfo?.oilCapacityQuarts;

        const computedLocationNotes = locationType
          ? `Location Type: ${locationType}\n${serviceLocationNotes || ""}`
          : serviceLocationNotes;
        const partsStores = useMultiplePartOrdersValue
          ? (() => {
              const stores =
                partOrdersData?.getPartOrders.data
                  ?.map((partOrder) => partOrder.store)
                  .filter((store) => store != null) || [];
              return uniqBy(prop("id"), stores);
            })()
          : pipe(
              pluck("partsStore", items),
              reject(anyPass([isEmpty, isNil])) as () => PartsStore[],
              uniqBy(prop("id"))
            );
        const contactId: string = prop("id", contact);
        return (
          <Form
            // padding bottom to offset save button
            className="pb-4"
          >
            <Paper className={"pb-3"}>
              {values.serviceCatalogueUsed === SERVICE_CATALOGUE_USED_SERVICES && (
                <ServicesSection
                  id={jobId}
                  services={services}
                  values={values}
                  setValues={setValues}
                  stage={JOB}
                  parentSetFieldValue={setFieldValue}
                  parentSubmitForm={submitForm}
                  parentSubmitting={isSubmitting}
                  initialValues={initialValues}
                  toggleSelectedServices={toggleSelectedServices}
                />
              )}
              <List>
                {!!values.preJobCheckListV2 && (
                  <ListItem divider>
                    <ListItemText
                      primary="Recommended Services"
                      secondary={
                        <ul className="list-disc list-inside">
                          <br />
                          <br />
                          <br />
                          {!!immediatly?.length ? (
                            <li>Service Recommended: {immediatly.map((s) => s.config.title).join(", ")}</li>
                          ) : null}
                          {!!soon?.length ? <li>Service Soon: {soon.map((s) => s.config.title).join(", ")}</li> : null}
                        </ul>
                      }
                    />
                    <ListItemSecondaryAction style={{ marginRight: 50, marginBottom: 20 }}>
                      <Button
                        color={"primary"}
                        variant={"outlined"}
                        disabled={sendingLink}
                        onClick={sendDIgitalInspection}
                      >
                        Send Inspection Report Link
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                )}
                <ContactListItem contact={values.contact} jobId={jobId} />
                {appointment && <AppointmentListItem appointment={appointment} />}
                <ServiceLocationListItem
                  address={serviceLocation}
                  serviceLocationAddress={job?.serviceLocationAddress}
                  notes={computedLocationNotes}
                />
                {/* VEHICLE-HISTORY (NUB-358): one door to job history — the tab, this row in page mode, or the old row. */}
                {showVehicleHistoryRow && (
                  <LinkListItem divider={true} title="Vehicle History" to={vehicleHistoryPath(vehicleId)} />
                )}
                {!showVehicleHistoryRow && !vehicleInOwnTab && (
                  <LinkListItem
                    divider={true}
                    title="Job History"
                    to={withReturnJob(`/jobs-history/${contactId}`, jobId)}
                  />
                )}
                <JobTypeListItem
                  jobType={type}
                  parentSubmitForm={submitForm}
                  parentSubmitting={isSubmitting}
                  isValid={isValid && hasChanges}
                  values={values}
                />
                {type === "Vioc Inspection" ? (
                  <JobViocStoreSelectField name={"viocStore"} label="Vioc Store" className="px-4 mt-4" />
                ) : null}
                <ListItemWithSubList divider={false} title={"Job Name"} secondaries={[jobName]} />
                <ListItemWithSubList divider={true} title={"Description"} secondaries={[description]} />
                {!vehicleInOwnTab && <VehicleInfoListItem vehicleId={vehicleId} contactId={contactId} />}
                {!vehicleInOwnTab && wheelTorque && (
                  <ListItem divider style={{ paddingRight: 90 }}>
                    <ListItemText primary={`Wheel torque`} secondary={<>{wheelTorque}</>} />
                  </ListItem>
                )}
                {!vehicleInOwnTab && oilCapacityQuarts && (
                  <ListItem divider style={{ paddingRight: 90 }}>
                    <ListItemText primary={`Oil Capacity (qts)`} secondary={<>{oilCapacityQuarts}</>} />
                  </ListItem>
                )}
                {/*<VehicleInfoSymptomsFormSection />*/}
                {customerExpectation && (
                  <ListItemWithSubList
                    divider={true}
                    title={"Customer Expectation for Repairs"}
                    secondaries={[customerExpectation]}
                  />
                )}
                {!!partOrders?.length && (
                  <>
                    <ListItem divider={false}>
                      <ListItemText
                        primary={<Typography style={{ fontWeight: "bold" }}>Parts Order(s)</Typography>}
                        secondary={partOrders.map((partOrder, idx) => {
                          return (
                            <PartsInfoListItem
                              key={idx}
                              partsLocation={partOrder.location}
                              partsNotes={partOrder.notes}
                              partsOrderNumber={partOrder.partsOrderNumber}
                            />
                          );
                        })}
                      />
                    </ListItem>
                  </>
                )}
                {!isEmpty(partsStores) && (
                  <>
                    <ListItem divider={false}>
                      <ListItemText
                        primary={<Typography style={{ fontWeight: "bold" }}>Parts Store(s)</Typography>}
                        secondary={partsStores.map((partsStore: any, idx: string | number | undefined) => {
                          return (
                            <PartsStoreListItem
                              key={idx}
                              partsStore={partsStore}
                              isHomePartsStore={equals(
                                (partsStore as any)?.id,
                                technicianGetMeData?.technicianGetMe?.homePartsStore?.id
                              )}
                            />
                          );
                        })}
                      />
                    </ListItem>
                  </>
                )}
                {values.serviceCatalogueUsed === SERVICE_CATALOGUE_USED_ITEMS && (
                  <>
                    <Container>
                      <div className="grid gap-4 pt-2">
                        <ItemsField
                          additionalPartsInfoRenderer={({ partsStore }) => {
                            if (partsStore) {
                              const homePartsStore = technicianGetMeData?.technicianGetMe?.homePartsStore;
                              return !homePartsStore || equals(partsStore.id, homePartsStore?.id) ? (
                                <Typography component={"span"} className={"block"} variant={"subtitle1"}>
                                  {partsStore.vendor} {partsStore.name}
                                </Typography>
                              ) : (
                                <Typography
                                  component={"span"}
                                  className={"block"}
                                  variant={"subtitle1"}
                                  style={{ color: "#f04e23" }}
                                >
                                  {partsStore.vendor} {partsStore.name}
                                </Typography>
                              );
                            }
                          }}
                          parentSubmit={submitForm}
                          setFieldValue={setFieldValue}
                          name={"items"}
                          editable={false}
                        />
                      </div>
                    </Container>
                    <Divider />
                  </>
                )}

                <ListItem divider={true}>
                  <TextAreaField name="privateNotes" label={"Private Notes"} />
                </ListItem>
                <FilesListItem divider={false} contactId={contactId} numFiles={fileNames.length} />
              </List>
            </Paper>
            <BottomSubmitButtonRow label={"Update Job"} isValid={isValid && dirty} isSubmitting={isSubmitting} />
          </Form>
        );
      }}
    </Formik>
  );
};
